<template>
    <div>
        <v-row>
            <v-col cols="5" class="d-flex pa-0">
                <v-card class="d-flex flex-column">
                    <v-card-title class="d-flex justify-space-between">
                        <div>Agentur</div>
                        <v-icon>fa-user-tie</v-icon>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="flex-grow-1 d-flex align-center justify-center">
                        <div style="width: 100%">
                            <v-autocomplete placeholder="Agentur" v-model="orgCode" :items="users" />
                            <div class="d-flex justify-center align-center">
                                <v-btn v-if="orgCode" @click="orgCode = null" color="red" dark class="darken-2" depressed>
                                    <v-icon left small>
                                        fa-times
                                    </v-icon>
                                    Fjern filter
                                </v-btn>
                                <div v-else>
                                    Søg efter et agentur i feltet ovenfor eller klik på et opkald på listen nedenunder for at se agenturets seneste opkald.
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-spacer />
            <v-col cols="6" class="pa-0 d-flex justify-end">
                <v-card>
                    <v-card-title class="d-flex justify-space-between">
                        <div>Måling</div>
                        <v-icon>fas fa-poll-h</v-icon>
                    </v-card-title>
                    <v-divider />
                    <v-card-text>
                        <div class="mb-2">
                            <h3>Type</h3>
                        </div>
                        <v-row>
                            <v-col cols="12">
                                <v-sheet>
                                    <v-slide-group show-arrows v-model="selectedCategory" mandatory>
                                        <v-slide-item
                                            v-for="(category, index) in categories"
                                            :key="index"
                                            v-slot:default="{ active, toggle }"
                                        >
                                            <v-btn
                                                class="mx-2"
                                                :input-value="active"
                                                active-class="third white--text"
                                                depressed
                                                @click="toggle"
                                            >
                                            {{ category.name }}
                                            </v-btn>
                                        </v-slide-item>
                                    </v-slide-group>
                                </v-sheet>
                            </v-col>
                        </v-row>
                        <v-divider class="mb-4" />
                        <div class="d-flex align-center justify-space-between mb-2">
                            <h3>
                                Skemaer
                            </h3>
                            <v-btn
                                v-if="categories[selectedCategory].surveys.length > 1"
                                depressed
                                x-small
                                class="ml-2"
                                @click="selectAllSurveys"
                                :input-value="selectedSurveys.length === categories[selectedCategory].surveys.length"
                                active-class="third darken-3 white--text"
                            >
                                Vælg alle
                                <v-icon x-small right>
                                    fa-check-double
                                </v-icon>
                            </v-btn>
                        </div>
                        <v-row>
                            <v-col cols="12">
                                <v-sheet>
                                    <v-slide-group show-arrows v-model="selectedSurveys" multiple mandatory>
                                        <v-slide-item
                                            v-for="(survey, index) in categories[selectedCategory].surveys"
                                            :key="index"
                                            v-slot:default="{ active, toggle }"
                                        >
                                            <v-btn
                                                class="mx-2"
                                                :input-value="active"
                                                active-class="third darken-3 white--text"
                                                depressed
                                                @click="toggle"
                                            >
                                            {{ survey.name }}
                                            </v-btn>
                                        </v-slide-item>
                                    </v-slide-group>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <br />
        <v-row v-if="orgCode">
            <v-card>
                <v-card-title class="d-flex justify-space-between">
                    <div>
                        Seneste opkald for {{ $store.state.main.userMap[orgCode] }}
                    </div>
                    <v-btn @click="getRecentCalls" color="primary" dark depressed>
                        <v-icon left>
                            fa-sync-alt
                        </v-icon>
                        Opdater
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-0">
                    <v-col class="pa-0">
                        <loader v-if="loading" />
                        <div v-else>
                            <v-subheader>
                                <v-row>
                                    <v-col cols="12" class="d-flex pa-0">
                                        <v-col
                                            v-for="(header, index) in recentCallsHeaders" :key="index"
                                            :cols="header.cols"
                                            :class="header.class"
                                            class="d-flex">
                                                {{ header.text }}
                                        </v-col>
                                        <v-spacer></v-spacer>
                                    </v-col>
                                </v-row>
                            </v-subheader>
                            <v-list class="recent-calls">
                                <v-list-item v-for="(call, index) in recentCalls" :key="index" class="pa-0">
                                    <v-col :cols="recentCallsHeaders[0].cols" :class="recentCallsHeaders[0].class" class="d-flex">
                                        <div v-line-clamp="1">
                                            {{ $store.state.main.campaignMap[call.campaign.uniqueId] }}
                                        </div>
                                    </v-col>
                                    <v-col :cols="recentCallsHeaders[1].cols" :class="recentCallsHeaders[1].class" class="d-flex">
                                        {{ formatStartTime(call.startTime) }}
                                    </v-col>
                                    <v-col :cols="recentCallsHeaders[2].cols" :class="recentCallsHeaders[2].class" class="d-flex">
                                        <v-chip
                                            v-if="call.inProgress"
                                            label
                                            color="primary"
                                        >
                                            I gang
                                        </v-chip>
                                        <v-chip
                                            v-else-if="call.conversationDuration && call.endCause === 'VoiceMail'"
                                            label
                                            color="yellow"
                                        >
                                            {{ toSeconds(call.conversationDuration) | duration }}
                                        </v-chip>
                                        <v-chip
                                            v-else-if="call.conversationDuration"
                                            label
                                            color="green"
                                        >
                                            {{ toSeconds(call.conversationDuration) | duration }}
                                        </v-chip>
                                        <v-chip
                                            v-else
                                            label
                                            color="red"
                                        >
                                            {{ toSeconds(0) | duration }}
                                        </v-chip>
                                    </v-col>
                                    <v-col :cols="recentCallsHeaders[3].cols" :class="recentCallsHeaders[3].class" class="d-flex">
                                        <a @click.stop target="_blank" :href="`https://hero01.herobase.com/NewItem/WebClient.aspx?id=${call.uniqueLeadId.slice(0, -5)}&mode=edit&arguments={}`">
                                            <v-icon style="cursor: pointer">
                                                fa-eye
                                            </v-icon>
                                        </a>
                                    </v-col>
                                    <v-col :cols="recentCallsHeaders[4].cols" :class="recentCallsHeaders[4].class" class="d-flex">
                                        <v-btn color="green" dark depressed @click.stop="newLog()">
                                            Registrer lyt
                                        </v-btn>
                                    </v-col>
                                </v-list-item>
                            </v-list>
                        </div>
                    </v-col>
                </v-card-text>
            </v-card>
        </v-row>
        <v-row v-else>
            <v-card>
                <v-card-title class="d-flex justify-space-between">
                    <div>Igangværende opkald</div>
                    <v-btn @click="getActiveCalls" color="primary" dark depressed>
                        <v-icon left>
                            fa-sync-alt
                        </v-icon>
                        Opdater
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-0">
                    <v-col class="pa-0">
                    <loader v-if="loading || !$store.state.main.userMap" />
                    <div v-else>
                        <v-subheader>
                            <v-row>
                                <v-col cols="12" class="d-flex pa-0">
                                    <v-col
                                        v-for="(header, index) in activeCallsHeaders" :key="index"
                                        :cols="header.cols"
                                        :class="header.class"
                                        class="d-flex">
                                            {{ header.text }}
                                    </v-col>
                                    <v-spacer></v-spacer>
                                </v-col>
                            </v-row>
                        </v-subheader>
                        <v-list class="active-calls">
                            <v-list-item v-for="(call, index) in filteredActiveCalls" :key="index" class="pa-0" @click="orgCode = call.user.orgCode">
                                <v-col :cols="activeCallsHeaders[0].cols" :class="activeCallsHeaders[0].class" class="d-flex">
                                    <div v-line-clamp="1">
                                        {{ $store.state.main.userMap[call.user.orgCode] }}
                                    </div>
                                </v-col>
                                <v-col :cols="activeCallsHeaders[1].cols" :class="activeCallsHeaders[1].class" class="d-flex">
                                    <div v-line-clamp="1">
                                        {{ $store.state.main.campaignMap[call.campaign.uniqueId] }}
                                    </div>
                                </v-col>
                                <v-col :cols="activeCallsHeaders[2].cols" :class="activeCallsHeaders[2].class" class="d-flex">
                                        {{ formatStartTime(call.startTime) }}
                                </v-col>
                                <v-col :cols="activeCallsHeaders[3].cols" :class="activeCallsHeaders[3].class" class="d-flex">
                                    <v-chip
                                        label
                                        color="primary"
                                    >
                                        I gang
                                    </v-chip>
                                </v-col>
                                <v-col :cols="activeCallsHeaders[4].cols" :class="activeCallsHeaders[4].class" class="d-flex">
                                    <a @click.stop target="_blank" :href="$hbLeadLink(call.uniqueLeadId)">
                                        <v-icon style="cursor: pointer">
                                            fa-eye
                                        </v-icon>
                                    </a>
                                </v-col>
                                <v-col :cols="activeCallsHeaders[5].cols" :class="activeCallsHeaders[5].class" class="d-flex">
                                    <v-btn color="green" dark depressed @click.stop="newLog(call.user.orgCode)">
                                        Registrer lyt
                                    </v-btn>
                                </v-col>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>
                </v-card-text>
            </v-card>
        </v-row>
        <v-dialog v-model="showSurveyDialog">
            <v-card>
                <v-card-title>Medlyt-rapport</v-card-title>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {parse, toSeconds} from 'iso8601-duration'
import moment from 'moment'

export default {
    data () {
        return {
            showSurveyDialog: false,
            surveySelectionExpanded: false,
            selectedCategory: 0,
            selectedSurveys: [0],
            survey: 1,
            categorySlideGroup: null,
            surveySlideGroup: null,
            questions: {
                abc: {
                    type: 'closed',
                    text: 'Oplyste agenturet den korrekte pris?'
                },
                def: {
                    type: 'score',
                    min: 1,
                    max: 5
                },
                ghi: {
                    type: 'close',
                    text: 'Oplyste agenturet den korrekte opstartsdato?'
                }
            },
            categories: [
                {
                    name: 'Kvalitet',
                    id: 1,
                    surveys: [
                        {
                            name: 'Standard',
                            id: 1,
                            questions: ['abc', 'def', 'ghi']
                        }
                    ]
                },
                {
                    name: 'Coaching',
                    id: 2,
                    surveys: [
                        {
                            name: 'Standard',
                            id: 1
                        }
                    ]
                },
                {
                    name: 'Akademi',
                    id: 3,
                    surveys: [
                        {
                            name: 'Modul 1',
                            id: 1
                        },
                        {
                            name: 'Modul 2',
                            id: 3
                        },
                        {
                            name: 'Modul 3',
                            id: 4
                        },
                        {
                            name: 'Modul 4',
                            id: 5
                        },
                        {
                            name: 'Modul 5',
                            id: 6
                        }
                    ]
                }
            ],
            loading: true,
            recentCalls: null,
            activeCalls: null,
            orgCode: null,
            recentCallsHeaders: [
                {
                    text: 'Kampagne',
                    cols: 5,
                    class: 'justify-start'
                },
                {
                    text: 'Start',
                    cols: 2,
                    class: 'justify-center'
                },
                {
                    text: 'Varighed',
                    cols: 2,
                    class: 'justify-center'
                },
                {
                    text: 'Emne',
                    cols: 1,
                    class: 'justify-center'
                },
                {
                    text: '',
                    cols: 2,
                    class: 'justify-center'
                }
            ],
            activeCallsHeaders: [
                {
                    text: 'Agentur',
                    cols: 3,
                    class: 'justify-start'
                },
                {
                    text: 'Kampagne',
                    cols: 3,
                    class: 'justify-start'
                },
                {
                    text: 'Start',
                    cols: 2,
                    class: 'justify-center'
                },
                {
                    text: 'Varighed',
                    cols: 1,
                    class: 'justify-center'
                },
                {
                    text: 'Emne',
                    cols: 1,
                    class: 'justify-center'
                },
                {
                    text: '',
                    cols: 2,
                    class: 'justify-center'
                }
            ]
        }
    },
    computed: {
        users () {
            return this.$store.getters["main/getUsers"]('Alle agenturer').map(user => {
                return {
                    text: user.name,
                    value: user.orgCode
                }
            })
        },
        filteredActiveCalls () {
            return this.activeCalls ? this.activeCalls.filter(call => this.$store.state.main.userMap[call.user.orgCode]) : []
        }
    },
    methods: {
        newLog (orgCode) {
            if (!orgCode) orgCode = this.orgCode
            this.showSurveyDialog = true
        },
        selectAllSurveys () {
            this.selectedSurveys =
                this.selectedSurveys.length === this.categories[this.selectedCategory].surveys.length
                ? [0]
                : [...Array(this.categories[this.selectedCategory].surveys.length).keys()]
        },
        async getRecentCalls () {
            this.loading = true
            const response = await this.$http().get('callLogs/recentCalls', {
                params: {
                    orgCode: this.orgCode
                }
            })
            this.recentCalls = response.data
            this.loading = false
        },
        async getActiveCalls () {
            this.loading = true
            const response = await this.$http().get('callLogs/activeCalls')
            this.activeCalls = response.data
            this.loading = false
        },
        toSeconds (value) {
            return value ? toSeconds( parse(value) ) : null
        },
        formatStartTime (startTime) {
            let minuteDiff = moment().diff(moment(startTime), 'minutes')
            const minuteString = minuteDiff > 1 ? 'minutter' : 'minut'
            let startTimeString
            if (minuteDiff > 0 && minuteDiff <= 59) {
                startTimeString = `${minuteDiff} ${minuteString} siden`
            } else if (minuteDiff > 0) {
                const hoursDiff = Math.floor(minuteDiff / 60)
                minuteDiff %= 60
                startTimeString = `${hoursDiff} t. og ${minuteDiff} m. siden`
            } else {
                startTimeString = 'Nu'
            }
            return startTimeString
        }
    },
    watch: {
        orgCode: function () {
            if (this.orgCode) this.getRecentCalls()
            else this.getActiveCalls()
        }
    },
    created () {
        this.getActiveCalls ()
    }
}
</script>

<style lang="scss">
.recent-calls, .active-calls {

    .v-list-item:nth-child(odd) {
        background: #f4f4f4;
    }

    a {
        text-decoration: none;
    }
}
.recent-calls {
    .v-chip__content {
        justify-content: center;
        min-width: 55px;
    }
}

</style>